import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { Settings, Save } from 'lucide-react';

export default function InvoiceSettings() {
  const { getAuthHeaders, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${config.API}/admin/invoice-settings`, { headers: getAuthHeaders() });
        setSettings(data);
      } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [getAuthHeaders]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const { data } = await axios.put(`${config.API}/admin/invoice-settings`, settings, { headers: getAuthHeaders() });
      setSettings(data);
      setMsg('Settings saved successfully!');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;

  const isSuperAdmin = user?.role === 'super_admin';
  const fields = [
    { key: 'business_name', label: 'Business Name' },
    { key: 'support_phone', label: 'Support Phone' },
    { key: 'support_email', label: 'Support Email' },
    { key: 'business_address', label: 'Business Address' },
    { key: 'gst_number', label: 'GST Number' },
    { key: 'invoice_heading', label: 'Invoice Heading' },
    { key: 'currency_symbol', label: 'Currency Symbol' },
    { key: 'footer_note', label: 'Footer / Disclaimer', textarea: true },
  ];

  return (
    <div data-testid="admin-invoice-settings" className="max-w-2xl space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Settings className="w-5 h-5 text-[#673c96]" />
        <h2 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Invoice Settings</h2>
      </div>

      {!isSuperAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
          Only super admins can edit invoice settings. Contact your admin for changes.
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 p-6 space-y-5">
        {msg && (
          <div data-testid="settings-message" className={`p-3 rounded-xl text-sm ${msg.includes('success') ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {msg}
          </div>
        )}

        {fields.map(f => (
          <div key={f.key}>
            <label className="text-xs font-medium text-[#311b52] block mb-1" style={{ fontFamily: 'Poppins' }}>{f.label}</label>
            {f.textarea ? (
              <textarea data-testid={`input-${f.key}`} value={settings?.[f.key] || ''} disabled={!isSuperAdmin}
                onChange={e => setSettings({...settings, [f.key]: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm focus:border-[#673c96] outline-none disabled:bg-gray-50 disabled:text-gray-500 min-h-[80px]" />
            ) : (
              <input data-testid={`input-${f.key}`} value={settings?.[f.key] || ''} disabled={!isSuperAdmin}
                onChange={e => setSettings({...settings, [f.key]: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm focus:border-[#673c96] outline-none disabled:bg-gray-50 disabled:text-gray-500" />
            )}
          </div>
        ))}

        {isSuperAdmin && (
          <button type="submit" disabled={saving} data-testid="save-settings-btn"
            className="bg-[#673c96] text-white hover:bg-[#3e195e] rounded-lg px-6 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </form>
    </div>
  );
}
